import { useState, useEffect } from 'react';

const DISMISS_KEY = 'fleamarkethub-pwa-dismissed';
const AUTO_HIDE_MS = 3000;

/** Survives React StrictMode remounts — Chrome only fires beforeinstallprompt once per load. */
let deferredInstallPrompt = null;
const subscribers = new Set();

function notify() {
  subscribers.forEach((fn) => fn());
}

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function wasDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* ignore */
  }
}

function hideBanner() {
  deferredInstallPrompt = null;
  setDismissed();
  notify();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    if (wasDismissed() || isStandalone()) return;
    deferredInstallPrompt = e;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    hideBanner();
  });
}

function InstallPWA() {
  const [, setTick] = useState(0);
  const showing = !isStandalone() && !wasDismissed() && !!deferredInstallPrompt;

  useEffect(() => {
    const refresh = () => setTick((n) => n + 1);
    subscribers.add(refresh);
    return () => subscribers.delete(refresh);
  }, []);

  // Auto-hide — do not use a "started" ref (StrictMode cleanup would cancel and never restart)
  useEffect(() => {
    if (!showing) return undefined;
    const t = window.setTimeout(() => {
      hideBanner();
    }, AUTO_HIDE_MS);
    return () => window.clearTimeout(t);
  }, [showing]);

  if (!showing) {
    return null;
  }

  const handleDismiss = () => {
    hideBanner();
  };

  const handleInstallClick = async () => {
    const promptEvent = deferredInstallPrompt;
    if (!promptEvent) return;

    hideBanner();

    promptEvent.prompt();
    try {
      await promptEvent.userChoice;
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-50 max-w-screen-md w-full px-4">
      <div className="bg-indigo-600 text-white rounded-lg shadow-lg p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <svg
            className="w-6 h-6 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          <div>
            <p className="font-semibold text-sm">Install FleaMarketHub</p>
            <p className="text-xs text-indigo-100">Add to your home screen for quick access</p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={handleDismiss}
            className="px-3 py-1 text-sm text-indigo-100 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={handleInstallClick}
            className="px-4 py-2 bg-white text-indigo-600 rounded-lg font-semibold text-sm hover:bg-indigo-50 active:bg-indigo-100 transition-colors"
            aria-label="Install app"
          >
            Install
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallPWA;
